import { Bookmark } from "lucide-react";
import { useT } from "../lib/i18n";
import { useUnits } from "../lib/useUnits";
import { LogRow } from "./LogRow";
import { QUICK_ADD_NAME, type FoodEntry, type MealSlot } from "@shared/types";
import type { MealGroup as MealGroupData } from "../lib/meals";

interface Props {
  /** Save the slot's logged entries as a reusable meal (undefined hides it). */
  onSaveMeal?: (slot: MealSlot) => void;
  group: MealGroupData;
  icon: string;
  target: number;
  onAdd: (slot: MealSlot) => void;
  onEdit: (entry: FoodEntry) => void;
  onDelete: (entry: FoodEntry) => void;
}

export function MealGroup({ group, icon, target, onAdd, onEdit, onDelete, onSaveMeal }: Props) {
  const t = useT();
  const units = useUnits();
  const eu = (kcal: number) => units.energy(kcal).value;
  const { slot, items, subtotal } = group;
  const pct = target > 0 ? Math.min(1, subtotal / target) : 0;
  const over = target > 0 && subtotal > target;

  return (
    <section className="glass rounded-2xl p-3.5">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="w-9 h-9 rounded-xl bg-surface-strong flex items-center justify-center text-lg flex-shrink-0">
          {icon}
        </span>
        <h3 className="text-sm font-semibold flex-1">{t(`meal.header.${slot}`)}</h3>
        <span className="text-sm tabular-nums">
          <span className={over ? "text-danger font-semibold" : "text-fg-muted"}>{eu(subtotal)}</span>
          <span className="text-fg-muted"> / {eu(target)}</span>
          {/* The number, not just the colour: ~8% of men have a colour-vision
              deficiency, so the overage must survive without hue (UI plan §4.5). */}
          {over && <span className="text-danger font-semibold"> +{eu(subtotal - target)}</span>}
        </span>
        {onSaveMeal && items.length > 0 && (
          <button
            onClick={() => onSaveMeal(slot)}
            aria-label={t("saved.save_as_meal")}
            className="w-8 h-8 rounded-full flex items-center justify-center text-fg-muted press"
          >
            <Bookmark size={16} aria-hidden="true" />
          </button>
        )}
        <button
          onClick={() => onAdd(slot)}
          aria-label={t("today.add")}
          className="w-8 h-8 rounded-full bg-brand text-on-brand text-xl leading-none flex items-center justify-center flex-shrink-0 press"
        >
          +
        </button>
      </div>

      <div className="h-1.5 rounded-full bg-surface-strong overflow-hidden mt-2.5">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{
            width: `${pct * 100}%`,
            // Red on over-target, at the owner's explicit request. This is a
            // deliberate deviation from UI plan §9 ("stated neutrally in grey,
            // never red"), whose intent was to avoid pressure around eating.
            // The rest of that rule still holds: no haptic, no alert, no
            // mascot reaction, and the overage is also given as a number so it
            // does not depend on colour alone (§4.5).
            background: over ? "rgb(var(--danger))" : "rgb(var(--brand))",
          }}
        />
      </div>

      {items.length > 0 && (
        <div className="space-y-2 mt-3">
          {items.map((e) => (
            <LogRow
              key={e.id}
              icon={icon}
              title={e.source === "quick" && e.name === QUICK_ADD_NAME ? t("quick.name") : e.name}
              value={units.energyStr(e.calories)}
              logged
              onTap={() => onEdit(e)}
              onLongPress={() => onDelete(e)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
